// Phase 83y.26 — Replace the legacy Bracket.Ops Shell fallback (which
// rendered the old tournament-creator UI on every unknown URL) with a
// minimal "Page not found" page that routes the user back to their
// role-appropriate landing.
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, House, Compass } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { firstAccessibleAdminPath } from "../lib/adminMenuCatalog";

export default function NotFound() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Mirror RootRedirect priority so an athlete lands on "/", admin on the
  // first menu they can access, referee on "/referee", guest on "/login".
  const homeUrl = (() => {
    if (!user) return "/login";
    const roles = user.roles || [];
    const hasAdmin =
      roles.includes("super_admin")
      || roles.includes("organizer")
      || roles.includes("admin")
      || roles.includes("staff");
    // Phase 85.A.273 — không cứng /admin/dashboard (tránh 403 loop).
    if (hasAdmin) return firstAccessibleAdminPath(user) || "/";
    if (roles.includes("referee")) return "/referee";
    return "/";
  })();

  useEffect(() => {
    document.title = "Không tìm thấy trang · PickLeague";
  }, []);

  return (
    <main
      className="min-h-screen bg-[#060B14] text-white flex flex-col items-center justify-center px-6 py-12"
      data-testid="not-found-page"
    >
      <div className="max-w-[420px] w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-[#FF8A00]/20 to-[#FF8A00]/5 border border-[#FF8A00]/40 flex items-center justify-center">
            <Compass size={36} weight="duotone" className="text-[#FF8A00]" />
          </div>
        </div>
        <div>
          <div className="text-[12px] font-black tracking-[0.3em] text-[#FF8A00]/85 uppercase">
            404 · Lạc đường
          </div>
          <h1 className="text-[26px] font-black text-white mt-2 leading-tight">
            Không tìm thấy trang này
          </h1>
          <p className="text-[13px] text-white/60 mt-3 leading-relaxed">
            Đường dẫn không tồn tại hoặc đã bị xoá. Bấm nút bên dưới để về
            trang chính.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={() => navigate(homeUrl, { replace: true })}
            className="h-12 px-6 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[13px] font-black tracking-wide shadow-[0_8px_24px_rgba(255,138,0,0.4)] hover:shadow-[0_8px_32px_rgba(255,138,0,0.55)] transition active:scale-[0.98] flex items-center justify-center gap-2"
            data-testid="not-found-home-btn"
          >
            <House size={16} weight="bold" />
            Về trang chính
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-12 px-6 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white/80 text-[13px] font-bold flex items-center justify-center gap-2 transition active:scale-[0.98]"
            data-testid="not-found-back-btn"
          >
            <ArrowLeft size={14} weight="bold" />
            Quay lại
          </button>
        </div>
      </div>
    </main>
  );
}
